import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, SlidersHorizontal, ChevronLeft, ChevronRight, PackagePlus } from "lucide-react";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Btn } from "../../components/Btn";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { StatusChip } from "../../components/dashboardModals";
import { type Application, type DeviceGroup } from "../../data";
import { createApp, listApps, listGroups, getAdminKpis } from "../../api/endpoints";
import { toApplication, toDeviceGroup } from "../../api/adapters";
import { useApiFallback } from "../../hooks/useApiFallback";
import { ApiError, type AppCreate } from "../../api/types";

// Палитра цветов для иконки — те же оттенки, что использует каталог.
const ICON_COLORS: { value: string; label: string }[] = [
  { value: "#1f3a8a", label: "navy"   },
  { value: "#7c3aed", label: "violet" },
  { value: "#0d9488", label: "teal"   },
  { value: "#d97706", label: "amber"  },
  { value: "#e11d48", label: "rose"   },
];

const APP_CATEGORIES = [
  "Работа",
  "Связь",
  "Документы",
  "Поле",
  "Безопасность",
  "Финансы",
];

type Tab = "all" | "published" | "revoked" | "draft";
const PAGE_SIZE = 50;

export default function AppsPage() {
  const qc = useQueryClient();
  const push = useToast();
  const [tab, setTab] = useState<Tab>("all");
  const [q, setQ] = useState("");
  const [developer, setDeveloper] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);

  const statusParam = tab === "all" ? undefined : tab;

  const appsQ = useQuery({
    queryKey: ["apps", { q, developer, status: statusParam, page }],
    queryFn: () => listApps({
      q: q || undefined,
      developer: developer || undefined,
      status: statusParam,
      page,
      page_size: PAGE_SIZE,
    }),
    placeholderData: keepPreviousData,
  });

  // Группы — нужны для селекта «Видимость» в модалке создания приложения.
  const groupsQ = useQuery({ queryKey: ["groups"], queryFn: listGroups });
  const groups: DeviceGroup[] = groupsQ.data?.map(toDeviceGroup) ?? [];

  // Реальные счётчики каталога — из /admin/kpis (он отдаёт apps_published, apps_revoked, apps_draft).
  // Раньше эти числа брались из захардкоженных констант data.ts (118/6/3).
  const kpisQ = useQuery({
    queryKey: ["admin", "kpis", "apps-tabs"],
    queryFn: getAdminKpis,
    staleTime: 30_000,
  });

  const createMut = useMutation({
    mutationFn: createApp,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["apps"] });
      await qc.invalidateQueries({ queryKey: ["admin", "kpis", "apps-tabs"] });
      push({ kind: "success", text: "Приложение создано" });
      setCreateOpen(false);
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.message || "Не удалось создать") : "Не удалось создать";
      push({ kind: "warn", text: msg });
    },
  });
  const totalCount     = appsQ.data?.meta.total ?? 0;
  const publishedCount = kpisQ.data?.apps_published ?? 0;
  const revokedCount   = kpisQ.data?.apps_revoked ?? 0;
  const draftCount     = kpisQ.data?.apps_draft ?? 0;

  const apiApps: Application[] | undefined = appsQ.data?.items.map(toApplication);
  // Пустой fallback — иначе при reload мелькает каталог демо-приложений.
  const apps = useApiFallback<Application[]>(apiApps, appsQ.isError, []);

  const total = appsQ.data?.meta.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const loading = appsQ.isLoading || appsQ.isFetching;

  function onChangeTab(t: Tab) {
    setTab(t);
    setPage(1);
  }

  function onChangeQ(v: string) {
    setQ(v);
    setPage(1);
  }

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Каталог приложений
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            {totalCount} приложений в каталоге
          </div>
        </div>
        <div className="flex gap-2">
          <Btn
            icon={SlidersHorizontal}
            variant={filtersOpen || developer ? "primary" : "secondary"}
            onClick={() => setFiltersOpen((value) => !value)}
          >
            Фильтры{developer ? " · 1" : ""}
          </Btn>
          <Btn icon={Plus} variant="primary" onClick={() => setCreateOpen(true)}>
            Новое приложение
          </Btn>
        </div>
      </header>

      {filtersOpen && (
        <section className="mb-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="min-w-0 flex-1">
              <span className="text-[12px] font-medium text-[var(--color-fg)]">Разработчик</span>
              <input
                value={developer}
                onChange={(event) => {
                  setDeveloper(event.target.value.trimStart());
                  setPage(1);
                }}
                placeholder="Логин, например petrov.a"
                className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
              />
            </label>
            <Btn
              variant="ghost"
              disabled={!developer}
              onClick={() => {
                setDeveloper("");
                setPage(1);
              }}
            >
              Сбросить
            </Btn>
          </div>
        </section>
      )}

      {/* Поиск */}
      <div className="relative">
        <Search
          size={15}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-fg-faint)]"
        />
        <input
          value={q}
          onChange={(e) => onChangeQ(e.target.value)}
          placeholder="Поиск по имени пакета или названию"
          className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] py-2.5 pl-9 pr-12 text-[13px] text-[var(--color-fg)] outline-none transition-colors placeholder:text-[var(--color-fg-faint)] focus:border-[var(--color-fg-muted)]"
        />
        <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-md bg-[var(--color-surface-2)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-fg-muted)] sm:inline">
          ⌘К
        </kbd>
      </div>

      {/* Табы */}
      <div className="mt-4 flex flex-wrap gap-1 border-b border-[var(--color-border)]">
        <TabBtn current={tab} value="all"       count={totalCount}     onSelect={onChangeTab}>Все</TabBtn>
        <TabBtn current={tab} value="published" count={publishedCount} onSelect={onChangeTab}>Опубликовано</TabBtn>
        <TabBtn current={tab} value="revoked"   count={revokedCount}   onSelect={onChangeTab}>Отозвано</TabBtn>
        <TabBtn current={tab} value="draft"     count={draftCount}     onSelect={onChangeTab}>Черновик</TabBtn>
      </div>

      {/* Таблица — на md+, карточки на mobile */}
      <div className={"mt-3 " + (loading ? "opacity-60 transition-opacity" : "transition-opacity")}>
        <div className="hidden md:block">
          <AppsTable apps={apps.value} />
        </div>
        <div className="flex flex-col gap-2 md:hidden">
          {apps.value.map((a, i) => (
            <AppMobileCard key={a.pkg} app={a} index={i} />
          ))}
          {!apps.value.length && <EmptyState q={q} />}
        </div>
      </div>

      {pageCount > 1 && (
        <Pagination page={page} pageCount={pageCount} onChange={setPage} total={total} />
      )}

      <CreateAppModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        groups={groups}
        onSubmit={(payload) => createMut.mutate(payload)}
        pending={createMut.isPending}
      />
    </>
  );
}

function CreateAppModal({
  open,
  onClose,
  groups,
  onSubmit,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  groups: DeviceGroup[];
  onSubmit: (payload: AppCreate) => void;
  pending: boolean;
}) {
  const [pkg, setPkg] = useState("");
  const [name, setName] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [visibility, setVisibility] = useState<string>("ALL");
  const [iconColor, setIconColor] = useState<string>(ICON_COLORS[0].value);
  const [iconLetter, setIconLetter] = useState<string>("А");
  const [category, setCategory] = useState<string>(APP_CATEGORIES[0]);

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- сброс формы при открытии модалки
      setPkg("");
      setName("");
      setShortDescription("");
      setVisibility("ALL");
      setIconColor(ICON_COLORS[0].value);
      setIconLetter("А");
      setCategory(APP_CATEGORIES[0]);
    }
  }, [open]);

  // Валидация: pkg — 2+ символа, латиница/цифры/точка/дефис; name — 2+ символа.
  const pkgValid = /^[a-zA-Z0-9._-]{2,}$/.test(pkg.trim());
  const nameValid = name.trim().length >= 2;
  const letterValid = iconLetter.trim().length >= 1;
  const canSubmit = pkgValid && nameValid && letterValid && !pending;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit({
      pkg: pkg.trim(),
      name: name.trim(),
      short_description: shortDescription.trim() || null,
      visibility,
      icon_color: iconColor,
      icon_letter: iconLetter.trim().slice(0, 1),
      category,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <PackagePlus size={16} className="text-[var(--color-brand)]" />
          Новое приложение
        </span>
      }
      subtitle="Создаст черновик в каталоге — версии и APK можно добавить позже"
      size="sm"
      footer={
        <>
          <Btn onClick={onClose}>Отмена</Btn>
          <Btn variant="primary" onClick={submit}>
            {pending ? "Создание…" : "Создать"}
          </Btn>
        </>
      }
    >
      <div className="space-y-3 text-[12.5px]">
        <Field label="Package" hint="латиница, цифры, точка">
          <input
            value={pkg}
            onChange={(e) => setPkg(e.target.value.replace(/\s+/g, ""))}
            placeholder="ru.astra.mail"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 font-mono text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </Field>
        <Field label="Название">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="АстраПочта"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </Field>
        <Field label="Краткое описание" hint="опционально">
          <textarea
            value={shortDescription}
            onChange={(e) => setShortDescription(e.target.value)}
            placeholder="Защищённая корпоративная почта"
            rows={2}
            className="w-full resize-none rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Видимость">
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 font-mono text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
            >
              <option value="ALL">ALL · Все сотрудники</option>
              {groups.filter((g) => g.code !== "ALL").map((g) => (
                <option key={g.code} value={g.code}>{g.code} · {g.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Категория">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
            >
              {APP_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-[1fr_88px] gap-3">
          <Field label="Цвет иконки">
            <select
              value={iconColor}
              onChange={(e) => setIconColor(e.target.value)}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 font-mono text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
            >
              {ICON_COLORS.map((c) => (
                <option key={c.value} value={c.value}>{c.value} · {c.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Буква">
            <div className="flex items-center gap-2">
              <input
                value={iconLetter}
                onChange={(e) => setIconLetter(e.target.value.slice(0, 1))}
                maxLength={1}
                className="w-12 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-center font-semibold text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
              />
              <span
                className="grid h-7 w-7 shrink-0 place-items-center rounded-[8px] font-semibold text-white"
                style={{ background: iconColor, fontSize: 13 }}
              >
                {iconLetter || "?"}
              </span>
            </div>
          </Field>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
          {label}
        </span>
        {hint && <span className="text-[10.5px] text-[var(--color-fg-faint)]">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function Pagination({
  page,
  pageCount,
  onChange,
  total,
}: {
  page: number;
  pageCount: number;
  onChange: (p: number) => void;
  total: number;
}) {
  return (
    <div className="mt-4 flex items-center justify-between gap-2 text-[12px] text-[var(--color-fg-muted)]">
      <div>Всего: <span className="font-mono text-[var(--color-fg)]">{total}</span></div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="grid h-7 w-7 place-items-center rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] disabled:opacity-40 hover:bg-[var(--color-surface-2)]"
          aria-label="назад"
        >
          <ChevronLeft size={14} />
        </button>
        <span className="px-2 font-mono">
          {page} / {pageCount}
        </span>
        <button
          onClick={() => onChange(Math.min(pageCount, page + 1))}
          disabled={page >= pageCount}
          className="grid h-7 w-7 place-items-center rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] disabled:opacity-40 hover:bg-[var(--color-surface-2)]"
          aria-label="вперёд"
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

function TabBtn({
  current,
  value,
  count,
  onSelect,
  children,
}: {
  current: Tab;
  value: Tab;
  count: number;
  onSelect: (t: Tab) => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      onClick={() => onSelect(value)}
      className={
        "relative -mb-px flex items-center gap-2 px-3 py-2 text-[13px] transition-colors " +
        (active
          ? "text-[var(--color-fg)]"
          : "text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]")
      }
    >
      <span className="font-medium">{children}</span>
      <span
        className={
          "rounded-full px-1.5 py-0.5 font-mono text-[10.5px] " +
          (active
            ? "bg-[var(--color-brand)] text-white"
            : "bg-[var(--color-surface-3)] text-[var(--color-fg-muted)]")
        }
      >
        {count}
      </span>
      {active && (
        <span className="absolute inset-x-0 -bottom-px h-[2px] rounded-full bg-[var(--color-brand)]" />
      )}
    </button>
  );
}

function AppsTable({ apps }: { apps: Application[] }) {
  const navigate = useNavigate();
  if (!apps.length) return <EmptyState q="" />;
  return (
    <div className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="bg-[var(--color-surface-2)] text-left text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
            <Th>Приложение</Th>
            <Th>Версия</Th>
            <Th>Поколение</Th>
            <Th>Видимость</Th>
            <Th>Разработчик</Th>
            <Th>Обновлено</Th>
            <Th>Статус</Th>
          </tr>
        </thead>
        <tbody>
          {apps.map((a, i) => (
            <tr
              key={a.pkg}
              onClick={() => navigate(`/admin/apps/${a.pkg}`)}
              className="stagger-up cursor-pointer border-t border-[var(--color-border)] transition-colors hover:bg-[var(--color-surface-2)]"
              style={{ animationDelay: `${Math.min(i, 8) * 35}ms` }}
            >
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-3">
                  <AppIcon app={a} />
                  <div className="min-w-0">
                    <div className="truncate font-medium text-[var(--color-fg)]">{a.name}</div>
                    <div className="truncate font-mono text-[11px] text-[var(--color-fg-muted)]">{a.pkg}</div>
                  </div>
                </div>
              </td>
              <td className="whitespace-nowrap px-3 py-2.5 font-mono">{a.version}</td>
              <td className="whitespace-nowrap px-3 py-2.5 font-mono text-[var(--color-fg-muted)]">{a.generation}</td>
              <td className="whitespace-nowrap px-3 py-2.5">
                <span className="rounded bg-[var(--color-surface-3)] px-1.5 py-0.5 font-mono text-[10.5px]">{a.visibility}</span>
              </td>
              <td className="whitespace-nowrap px-3 py-2.5 font-mono text-[var(--color-fg-muted)]">{a.developer}</td>
              <td className="whitespace-nowrap px-3 py-2.5 text-[var(--color-fg-muted)]">{a.updated}</td>
              <td className="whitespace-nowrap px-3 py-2.5">
                {a.status === "revoked" && a.revokedReason ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-danger-bg)] px-2 py-0.5 text-[10.5px] font-medium text-[var(--color-danger)]">
                    <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: "var(--color-danger)" }} />
                    Отозвано · {a.revokedReason}
                  </span>
                ) : (
                  <StatusChip status={a.status} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}

function AppIcon({ app, size = 32 }: { app: Application; size?: number }) {
  return (
    <span
      className="grid shrink-0 place-items-center rounded-[8px] font-semibold text-white"
      style={{ background: app.iconColor, width: size, height: size, fontSize: size * 0.42 }}
    >
      {app.iconLetter}
    </span>
  );
}

function AppMobileCard({ app, index }: { app: Application; index: number }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(`/admin/apps/${app.pkg}`)}
      className="stagger-up flex w-full items-start gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.15)]"
      style={{ animationDelay: `${Math.min(index, 8) * 35}ms` }}
    >
      <AppIcon app={app} size={40} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-[14px] font-medium text-[var(--color-fg)]">{app.name}</div>
            <div className="truncate font-mono text-[11px] text-[var(--color-fg-muted)]">{app.pkg}</div>
          </div>
          {app.status === "revoked" ? (
            <span className="shrink-0 rounded-full bg-[var(--color-danger-bg)] px-2 py-0.5 text-[10px] font-medium text-[var(--color-danger)]">
              отозвано
            </span>
          ) : (
            <StatusChip status={app.status} />
          )}
        </div>
        <div className="mt-1.5 flex items-center gap-2 text-[11.5px] text-[var(--color-fg-muted)]">
          <span className="font-mono">{app.version}</span>
          <span>·</span>
          <span className="font-mono">g.{app.generation}</span>
          <span>·</span>
          <span>{app.updated}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5 text-[10.5px]">
          <span className="rounded bg-[var(--color-surface-3)] px-1.5 py-0.5 font-mono">{app.visibility}</span>
          <span className="rounded bg-[var(--color-surface-3)] px-1.5 py-0.5 font-mono text-[var(--color-fg-muted)]">{app.developer}</span>
        </div>
      </div>
    </button>
  );
}

function EmptyState({ q }: { q: string }) {
  return (
    <div className="grid place-items-center rounded-xl border border-dashed border-[var(--color-border-strong)] bg-[var(--color-surface)] px-6 py-12 text-center">
      <div className="text-[13.5px] font-medium text-[var(--color-fg)]">Ничего не найдено</div>
      <div className="mt-1 text-[12px] text-[var(--color-fg-muted)]">
        {q ? `По запросу «${q}» — приложения отсутствуют. Попробуй другой запрос или сменить вкладку.` : "В этой вкладке пока нет приложений."}
      </div>
    </div>
  );
}
